import cs from "classnames";
import React from "react";
import { AiOutlineClockCircle, AiOutlineUser } from "react-icons/ai";
import { BsFileText } from "react-icons/bs";
import { FormatDateOptions, useIntl } from "react-intl";

import { Attachment } from "lib/build/attachments";
import { Origin } from "lib/build/events";

import { AttachmentTable } from "src/common/components/AttachmentTable";
import { Popover } from "src/common/components/Popover";
import { Tooltip } from "src/common/components/Tooltip";
import { useFormatName } from "src/common/hooks/useFormatName";
import { Coworker, useKnownUsers } from "src/features/auth";
import { useOrgModParams } from "src/features/orgmod";
import { PatientLink } from "src/features/patient";
import { sidToForms } from "src/sidToForms";

import "./Record.css";

const formatDateOptions: FormatDateOptions = {
	year: "numeric",
	month: "numeric",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	second: "numeric"
};

export const RecordGroupContainer: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => <div className="record-group-container">{children}</div>;

interface RecordGroupProps {
	variant?: "light" | "dark" | "primary";
	label: React.ReactNode;
}

export const RecordGroup: React.FunctionComponent<
	React.PropsWithChildren<RecordGroupProps>
> = React.memo(({ variant = "light", label, children }) => (
	<div className={`record-group record-group--${variant}`}>
		<div className="record-group__label">{label}</div>
		<div className="record-group__content">{children}</div>
	</div>
));

type ShortRecordProps = Omit<RecordProps, "variant">;

export const ShortRecord: React.FunctionComponent<ShortRecordProps> = props => (
	<Record variant="short" {...props} />
);

type LongRecordProps = Omit<RecordProps, "variant">;

export const LongRecord: React.FunctionComponent<LongRecordProps> = props => (
	<Record variant="long" {...props} />
);

interface RecordProps {
	variant?: "short" | "long";
	alignment?: "left" | "right" | "center";
	label?: React.ReactNode;
	attachments?: Attachment[];
	value?: React.ReactNode;
	timestamp?: string;
	sid?: string;
	origin?: Origin;
}

export const Record: React.FunctionComponent<RecordProps> = React.memo(
	({
		variant = "short",
		alignment = "left",
		label,
		attachments,
		value,
		timestamp,
		sid,
		origin
	}) => {
		const users = useKnownUsers();

		const { formatDate } = useIntl();
		const formatName = useFormatName();

		const link = useLink(sid, origin);

		const title = renderTitle({
			users,
			timestamp,
			origin,
			formatDate,
			formatName
		});

		const content = (
			<div
				className={cs(
					"record",
					`record--${variant}`,
					`record--${alignment}`,
					{
						"record--with-label": !!label,
						"record--with-value": !!value
					}
				)}
			>
				{label !== undefined && (
					<div className="record__label">
						{label}
						{!!attachments && attachments.length > 0 && (
							<Popover
								target={<BsFileText />}
								content={
									<AttachmentTable
										attachments={attachments}
									/>
								}
							/>
						)}
					</div>
				)}
				{value && (
					<div className="record__value">
						{typeof value === "string"
							? value.split("\n").map((line, i) =>
									i === 0 ? (
										<React.Fragment key={i}>
											{line}
										</React.Fragment>
									) : (
										<React.Fragment key={i}>
											<br />
											{line}
										</React.Fragment>
									)
								)
							: value}
					</div>
				)}
			</div>
		);

		if (link === "") {
			return <Tooltip title={title} content={content} />;
		}

		return (
			<Tooltip
				title={title}
				content={
					<PatientLink to={link} className="record-link">
						{content}
					</PatientLink>
				}
			/>
		);
	}
);

interface GroupedRecordListProps {
	timestamp?: string;
	sid?: string;
	origin?: Origin;
}

export const GroupedRecordList: React.FunctionComponent<
	React.PropsWithChildren<GroupedRecordListProps>
> = React.memo(({ children, timestamp, sid, origin }) => {
	const users = useKnownUsers();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	const link = useLink(sid, origin);

	const content = <div className="grouped-record-list">{children}</div>;

	if (link === "") {
		return <Tooltip title={title} content={content} />;
	}

	return (
		<Tooltip
			title={title}
			content={
				<PatientLink to={link} className="grouped-record-list-link">
					{content}
				</PatientLink>
			}
		/>
	);
});

interface GroupedRecordListGroupProps {
	label?: React.ReactNode;
	attachments?: Attachment[];
}

export const GroupedRecordListGroup: React.FunctionComponent<
	React.PropsWithChildren<GroupedRecordListGroupProps>
> = React.memo(({ label, attachments, children }) => (
	<div className="record-list-group">
		{label && (
			<div className="record-list__label">
				{label}
				{!!attachments && attachments.length > 0 && (
					<Popover
						target={<BsFileText />}
						content={<AttachmentTable attachments={attachments} />}
					/>
				)}
			</div>
		)}
		<table>
			<tbody>{children}</tbody>
		</table>
	</div>
));

interface RecordListProps {
	label?: React.ReactNode;
	attachments?: Attachment[];
	timestamp?: string;
	sid?: string;
	origin?: Origin;
}

export const RecordList: React.FunctionComponent<
	React.PropsWithChildren<RecordListProps>
> = React.memo(({ attachments, label, children, timestamp, sid, origin }) => {
	const users = useKnownUsers();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const link = useLink(sid, origin);

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	const content = (
		<div className="record-list">
			{label && (
				<div className="record-list__label">
					{label}
					{!!attachments && attachments.length > 0 && (
						<Popover
							target={<BsFileText />}
							content={
								<AttachmentTable attachments={attachments} />
							}
						/>
					)}
				</div>
			)}
			<table>
				<tbody>{children}</tbody>
			</table>
		</div>
	);

	if (link === "") {
		return <Tooltip title={title} content={content} />;
	}

	return (
		<Tooltip
			title={title}
			content={
				<PatientLink to={link} className="record-list-link">
					{content}
				</PatientLink>
			}
		/>
	);
});

interface RecordListItemProps {
	label?: React.ReactNode;
	value?: React.ReactNode;
	timestamp?: string;
	sid?: string;
	origin?: Origin;
}

export const RecordListItem: React.FunctionComponent<RecordListItemProps> =
	React.memo(({ label, value, timestamp, sid, origin }) => {
		const users = useKnownUsers();

		const { formatDate } = useIntl();
		const formatName = useFormatName();

		const link = useLink(sid, origin);

		const title = renderTitle({
			users,
			timestamp,
			origin,
			formatDate,
			formatName
		});

		const content = <div className="record-list__value">{value}</div>;

		return (
			<tr>
				<td>{label}</td>
				<td>
					<Tooltip
						leftOffset={-5}
						topOffset={-5}
						title={title}
						content={
							link === "" ? (
								content
							) : (
								<PatientLink
									to={link}
									className="record-list-item-link"
								>
									{content}
								</PatientLink>
							)
						}
					/>
				</td>
			</tr>
		);
	});

interface RecordTableContainerProps {
	label?: React.ReactNode;
	attachments?: Attachment[];
	timestamp?: string;
	sid?: string;
	origin?: Origin;
}

export const RecordTableContainer: React.FunctionComponent<
	React.PropsWithChildren<RecordTableContainerProps>
> = React.memo(({ label, attachments, children, timestamp, sid, origin }) => {
	const users = useKnownUsers();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const link = useLink(sid, origin);

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	const content = (
		<div className="record-table">
			{label && (
				<div className="record-table__label">
					{label}
					{!!attachments && attachments.length > 0 && (
						<Popover
							target={<BsFileText />}
							content={
								<AttachmentTable attachments={attachments} />
							}
						/>
					)}
				</div>
			)}
			<div className="record-table__content">{children}</div>
		</div>
	);

	if (link === "") {
		return <Tooltip title={title} content={content} />;
	}

	return (
		<Tooltip
			title={title}
			content={
				<PatientLink to={link} className="record-table-link">
					{content}
				</PatientLink>
			}
		/>
	);
});

interface TitleProps {
	users: { [key: string]: Coworker };
	timestamp?: string;
	origin?: Origin;
	formatDate(date: string, options: FormatDateOptions): string;
	formatName(options: {
		title: string;
		firstName: string;
		lastName: string;
	}): string;
}

function renderTitle({
	users,
	timestamp,
	origin,
	formatDate,
	formatName
}: TitleProps): React.ReactNode {
	const content: React.ReactNode[] = [];
	if (timestamp !== undefined) {
		content.push(
			<div className="record-title__line">
				<div className="record-title__icon">
					<AiOutlineClockCircle />
				</div>
				{formatDate(timestamp, formatDateOptions)}
			</div>
		);
	}
	if (origin !== undefined && origin.recordedBy in users) {
		content.push(
			<div className="record-title__line">
				<div className="record-title__icon">
					<AiOutlineUser />
				</div>
				{formatName(users[origin.recordedBy])}
			</div>
		);
	}
	if (content.length === 0) {
		return null;
	}
	return (
		<div className="record-title">
			{content.map((line, i) => (
				<React.Fragment key={i}>{line}</React.Fragment>
			))}
		</div>
	);
}

function useLink(sid: string | undefined, origin: Origin | undefined): string {
	const { mod } = useOrgModParams();
	return React.useMemo(() => {
		if (sid !== undefined) {
			if (mod in sidToForms) {
				if (sid in sidToForms[mod]) {
					return `/form/${sidToForms[mod][sid][0].id}`;
				}
			}
		}
		if (origin !== undefined) {
			return `/form/${origin.kind}/${origin.id}`;
		}
		return "";
	}, [mod, sid, origin]);
}
